import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isFirstResponderUpgradePlan, isMilitaryUpgradePlan } from 'Utils/SheerIdHelpers';
import {
  getPlanDisplayPrice,
  getPlanFromList,
  getPlanSelectFrequencyLabel,
  getSimplifiedPlanDisplayName,
} from 'Utils/Plans';
import { resetPromoCode } from 'State/PromoCode/PromoCodeActions';
import { PlanSelectionModal } from 'Features/Plans/PlanSelection/PlanSelection';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import './PlanPreview.css';
import { isEmptyObj } from 'Utils/Utils';
import { HTMLParser } from 'Utils/Parser';

const Price = ({ price, freqLabel }) => {
  if (!price) return null;

  return (
    <div className="price">
      {price}
      <span className="planFrequency">{HTMLParser(freqLabel)}</span>
    </div>
  );
};

const LargePrice = ({ price, freqLabel }) => {
  if (!price) return null;

  return <div className="largePrice">{HTMLParser(`${price}${freqLabel}`)}</div>;
};

//Always showing change Plans button even if only 1 selectable plan since sign up plans screen shows more plan details.
export const PlanPreview = ({ plan, isSheerIdFree, subscription, promoMsg, selectPlan, allPlans }) => {
  const { hasActiveSubscription, activeSubscriptionId, nextSubscription, validityTill } = subscription;
  const [showModal, toggleModal] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetPromoCode());
    };
  }, [dispatch]);

  if (!plan) return null;

  /*
  TODO: Can probably simplify & improve isStandalonePrice logic to look at plan.hasPrePayPromotion instead
  Same for all of the logic in "planPreviewRight" div below 
  */

  //No "then" in front of the price and has slightly different format
  const isStandalonePrice =
    isMilitaryUpgradePlan(plan.packageID) ||
    isFirstResponderUpgradePlan(plan.packageID) ||
    (promoMsg && hasActiveSubscription);

  const { paymentPromotionFeature, promotionPlanName, paymentPriceOverride, price, frequencyLabel } = plan.dppContent;

  const displayPrice = price || getPlanDisplayPrice(plan.planPrice);
  const freqLabel = frequencyLabel === 'false' ? '' : frequencyLabel || getPlanSelectFrequencyLabel(plan);
  const planHighlight = promoMsg ? promoMsg : paymentPromotionFeature;
  const pricePrefix = plan?.dppContent?.pricePrefix === 'false' ? '' : 'then';

  let activePlan = getPlanFromList(activeSubscriptionId, allPlans);
  activePlan = isEmptyObj(activePlan) ? null : activePlan;

  const handleChangePlanEvent = () => {
    trackEvent(TRACKING_ENUMS.EVENT_CHANGE_PLAN_STARTED, {
      page_start_source: 'payment',
      page_plan_name: plan.packageID,
    });
  };

  const changePlanButton = (
    <button
      className="changePlanLink"
      onClick={() => {
        handleChangePlanEvent();
        toggleModal(true);
      }}
    >
      <span className="changePlanLinkText">Change Plan</span>
    </button>
  );

  return (
    <>
      <div className="planPreview">
        <div className="planPreviewLeft">
          <div className="name">{HTMLParser(promotionPlanName || getSimplifiedPlanDisplayName(plan))}</div>
          {!isSheerIdFree && allPlans.length > 1 && changePlanButton}
        </div>
        <div className="planPreviewRight">
          {hasActiveSubscription && !promoMsg && !isSheerIdFree && (
            <LargePrice price={displayPrice} freqLabel={freqLabel} />
          )}
          {planHighlight && (!hasActiveSubscription || !!promoMsg || isSheerIdFree) && (
            <div className="planHighlight">{HTMLParser(planHighlight)}</div>
          )}
          {((!isSheerIdFree && !hasActiveSubscription) || promoMsg) && (
            <div className="previewBottomRight">
              {!isStandalonePrice && !paymentPriceOverride && (
                <div className={`changePlanLinkText pricingSubtitle`}>{HTMLParser(pricePrefix)}</div>
              )}
              {!paymentPriceOverride && <Price price={displayPrice} freqLabel={freqLabel} />}
              {paymentPriceOverride && (
                <div className="specialPreviewBottomRight">{HTMLParser(paymentPriceOverride)}</div>
              )}
            </div>
          )}
        </div>
      </div>
      <PlanSelectionModal
        show={showModal}
        selectPlan={({ planId }) => {
          selectPlan(planId);
          toggleModal(false);
        }}
        onClose={() => toggleModal(false)}
        selectedPlan={plan}
        plans={allPlans}
        currentPlan={activePlan}
        upcomingPlan={nextSubscription ? getPlanFromList(nextSubscription.serviceID, allPlans) : null}
        validityTill={validityTill}
      />
    </>
  );
};
